import { CustomLayerInterface, CustomRenderMethodInput, GeoJSONSourceSpecification, Map as MlMap } from "maplibre-gl";
import { setGeoJsonData } from "./webgl-maplibre.layers";
import { lngLatElevToMercator, Obstacle3dLayer } from "./webgl-maplibre.obstacle-3d-layer";

const LINE_SOURCES = { "route-planned": { width: 15, alpha: 0.4 }, "route-active": { width: 5, alpha: 1 } };
const POINT_SOURCE = "route-points";
const SOURCE_IDS = [...Object.keys(LINE_SOURCES), POINT_SOURCE];
const empty = (): GeoJSON.FeatureCollection => ({ type: "FeatureCollection", features: [] });

/** Segment endpoints of every line in `data`, as [lng, lat] pairs. */
export function routeSegments(data: GeoJSON.FeatureCollection): number[][][] {
  const segments: number[][][] = [];
  for (const { geometry } of data.features) {
    const lines =
      geometry?.type === "LineString"
        ? [geometry.coordinates]
        : geometry?.type === "MultiLineString"
          ? geometry.coordinates
          : [];
    for (const line of lines) for (let i = 1; i < line.length; i++) segments.push([line[i - 1], line[i]]);
  }
  return segments;
}

/**
 * Connect route waypoints at their individual ground elevations. Lines and
 * labels share the same elevation lookup and main-map projection.
 *
 * The lines draw on their own small WebGL canvas, above the chart planes, which
 * are separate canvases too. A hook layer in the main map draws them with the
 * main map's matrix, in the same frame. Waypoint markers and names are HTML,
 * like the approach sector labels: custom layers cannot use MapLibre's glyphs.
 */
export class RoutePlane {
  private data = new Map<string, GeoJSON.FeatureCollection>();
  private layers = new Map<string, Obstacle3dLayer>();
  // `shown` is the last transform written, so a frame writes only moved markers.
  private points: { element: HTMLDivElement; lng: number; lat: number; shown: string }[] = [];
  private lineSignatures = new Map<string, string>();
  private canvas = document.createElement("canvas");
  private gl: WebGLRenderingContext | null;
  private drawn = false;
  // Without a context the route stays on the 2D layers: setData returns false.
  private failed = false;
  private hook: CustomLayerInterface = {
    id: "route-plane",
    type: "custom",
    renderingMode: "3d",
    render: (_gl, input) => this.frame(input),
  };

  constructor(
    private readonly map: MlMap,
    private readonly altitude: (point: number[]) => number
  ) {
    const options = { alpha: true, premultipliedAlpha: true, antialias: false, depth: false };
    this.gl = (this.canvas.getContext("webgl2", options) ??
      this.canvas.getContext("webgl", options)) as WebGLRenderingContext | null;
    try {
      if (!this.gl) throw new Error("no WebGL context for the 3D route");
      // The planned route draws first, under the active route.
      for (const [id, { width }] of Object.entries(LINE_SOURCES)) {
        const layer = new Obstacle3dLayer(`${id}-3d`, 1, width, false);
        this.layers.set(id, layer);
        layer.onAdd(map, this.gl);
      }
    } catch (err) {
      // Browsers cap live WebGL contexts. Keep the 2D route rather than none.
      console.warn("route plane: 3D route unavailable, keeping 2D", err);
      this.failed = true;
      return;
    }
    // Empty the 2D route only now that the 3D route can draw it.
    for (const id of SOURCE_IDS) {
      const source = map.getSource(id)?.serialize() as GeoJSONSourceSpecification | undefined;
      this.data.set(id, typeof source?.data === "object" ? (source.data as GeoJSON.FeatureCollection) : empty());
      setGeoJsonData(map, id, empty());
    }
    // z-index 1 puts the route above the chart planes, which have none.
    Object.assign(this.canvas.style, { position: "absolute", inset: "0", pointerEvents: "none", zIndex: "1" });
    this.canvas.setAttribute("aria-hidden", "true");
    this.canvas.dataset.routePlane = "3d";
    this.canvas.addEventListener("webglcontextlost", this.fallBackTo2d);
    map.getCanvasContainer().appendChild(this.canvas);
    map.addLayer(this.hook);
    for (const id of SOURCE_IDS) this.update(id);
  }

  setData(id: string, data: GeoJSON.FeatureCollection): boolean {
    if (this.failed || !SOURCE_IDS.includes(id)) return false;
    this.data.set(id, data);
    this.update(id);
    this.map.triggerRepaint();
    return true;
  }

  private update(id: string): void {
    const data = this.data.get(id) ?? empty();
    if (id === POINT_SOURCE) return this.setPoints(data);
    const layer = this.layers.get(id);
    const { alpha } = LINE_SOURCES[id as keyof typeof LINE_SOURCES];
    const segments = routeSegments(data)
      .map(([a, b]) => [
        [a[0], a[1], this.altitude(a)],
        [b[0], b[1], this.altitude(b)],
      ])
      .filter(([a, b]) => Number.isFinite(a[2]) && Number.isFinite(b[2]));
    const signature = JSON.stringify(segments);
    if (this.lineSignatures.get(id) === signature) return;
    this.lineSignatures.set(id, signature);
    const lines = new Float64Array(segments.length * 6);
    segments.forEach(([a, b], i) => {
      lines.set(lngLatElevToMercator(a[0], a[1], a[2]), i * 6);
      lines.set(lngLatElevToMercator(b[0], b[1], b[2]), i * 6 + 3);
    });
    // Magenta, as the 2D route layers draw it. The shader premultiplies.
    const colors = new Float32Array(segments.length * 4);
    for (let i = 0; i < segments.length; i++) colors.set([1, 0, 1, alpha], i * 4);
    layer?.setData(lines, new Float64Array(0), new Float64Array(0), new Float64Array(0), colors);
  }

  private setPoints(data: GeoJSON.FeatureCollection): void {
    for (const point of this.points) point.element.remove();
    this.points = [];
    for (const feature of data.features) {
      if (feature.geometry?.type !== "Point") continue;
      const p = feature.properties ?? {};
      const radius = Number(p.radius ?? 4);
      const element = document.createElement("div");
      Object.assign(element.style, {
        position: "absolute",
        left: "0",
        top: "0",
        display: "none",
        pointerEvents: "none",
        zIndex: "1",
        width: `${radius * 2}px`,
        height: `${radius * 2}px`,
        borderRadius: "50%",
        background: p.fillColor ?? "#fff",
        border: `2px solid ${p.strokeColor ?? "#fff"}`,
      });
      if (p.displayName) {
        const label = document.createElement("div");
        label.textContent = String(p.displayName);
        // 2.2 em below the point, like the 2D `text-offset`.
        Object.assign(label.style, {
          position: "absolute",
          left: "50%",
          top: "calc(50% + 30.8px)",
          transform: "translate(-50%, -50%)",
          color: "#f0f",
          font: "bold 14px 'Libre Franklin', sans-serif",
          whiteSpace: "nowrap",
          textShadow: "0 0 3px rgba(0,0,0,0.5), 0 0 5px rgba(0,0,0,0.5)",
        });
        element.appendChild(label);
      }
      this.map.getCanvasContainer().appendChild(element);
      const [lng, lat] = feature.geometry.coordinates;
      this.points.push({ element, lng, lat, shown: "none" });
    }
  }

  // ponytail: a lost context is not restored; the route stays 2D until 3D is switched off and on.
  private fallBackTo2d = (): void => {
    if (this.failed) return;
    this.failed = true;
    this.setPoints(empty());
    for (const [id, data] of this.data) setGeoJsonData(this.map, id, data);
    this.data.clear();
    this.map.triggerRepaint();
  };

  private frame = (input: CustomRenderMethodInput): void => {
    if (this.failed) return;
    for (const id of Object.keys(LINE_SOURCES)) this.update(id);
    this.canvas.style.visibility = [...this.lineSignatures.values()].some((value) => value !== "[]")
      ? "visible"
      : "hidden";
    this.draw(input);
    const m = input.defaultProjectionData.mainMatrix;
    const canvas = this.map.getCanvas();
    for (const point of this.points) {
      const altitude = this.altitude([point.lng, point.lat]);
      if (!Number.isFinite(altitude)) {
        point.element.style.display = "none";
        point.shown = "none";
        continue;
      }
      const [x, y, z] = lngLatElevToMercator(point.lng, point.lat, altitude);
      const w = m[3] * x + m[7] * y + m[11] * z + m[15];
      const px = (m[0] * x + m[4] * y + m[8] * z + m[12]) / w;
      const py = (m[1] * x + m[5] * y + m[9] * z + m[13]) / w;
      const visible = w > 0 && Math.abs(px) <= 1.1 && Math.abs(py) <= 1.1;
      const left = Math.round(((px + 1) * canvas.clientWidth) / 2);
      const top = Math.round(((1 - py) * canvas.clientHeight) / 2);
      const shown = visible ? `translate(${left}px, ${top}px) translate(-50%, -50%)` : "none";
      if (shown === point.shown) continue;
      if (!visible) point.element.style.display = "none";
      else {
        if (point.shown === "none") point.element.style.display = "block";
        point.element.style.transform = shown;
      }
      point.shown = shown;
    }
  };

  private draw(input: CustomRenderMethodInput): void {
    const gl = this.gl;
    if (!gl) return;
    const hasLines = this.data.get("route-planned")?.features.length || this.data.get("route-active")?.features.length;
    if (!hasLines && !this.drawn) return;
    const main = this.map.getCanvas();
    if (this.canvas.width !== main.width || this.canvas.height !== main.height) {
      this.canvas.width = main.width;
      this.canvas.height = main.height;
    }
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    // Keep the real altitude's screen position and perspective, but place the
    // overlay inside the clip-depth range. Terrain-derived near/far planes can
    // otherwise clip a route below the terrain even with depth testing off.
    const matrix = Float64Array.from(input.defaultProjectionData.mainMatrix);
    for (const index of [2, 6, 10, 14]) matrix[index] = 0;
    const overlayInput = {
      ...input,
      defaultProjectionData: {
        ...input.defaultProjectionData,
        mainMatrix: matrix as unknown as typeof input.defaultProjectionData.mainMatrix,
      },
    };
    for (const layer of this.layers.values()) layer.render(gl, overlayInput);
    this.drawn = !!hasLines;
  }

  remove(): void {
    this.canvas.removeEventListener("webglcontextlost", this.fallBackTo2d);
    this.failed = true;
    if (this.map.getLayer(this.hook.id)) this.map.removeLayer(this.hook.id);
    for (const layer of this.layers.values()) layer.onRemove();
    this.layers.clear();
    // Browsers cap live WebGL contexts, so free this one now, not at GC.
    this.gl?.getExtension("WEBGL_lose_context")?.loseContext();
    this.gl = null;
    this.canvas.remove();
    this.setPoints(empty());
    for (const [id, data] of this.data) setGeoJsonData(this.map, id, data);
    this.data.clear();
  }
}
